/**
 * The formatting layer.
 *
 * Every value that reaches the screen goes through one of these. They are pure,
 * total functions: given `null`, `undefined`, `NaN` or `Infinity` they return an
 * em dash. Nothing downstream is allowed to call `.toFixed()` on a nullable —
 * that is how `NaN` ends up in front of an analyst.
 *
 * ── Why this lives in shared ────────────────────────────────────────────────
 *
 * Same reason as `periods.ts`: two sides must agree. The client renders these
 * strings; the SERVER now also needs them, because the AI-insight guard checks
 * that every number in a generated card matches a figure the model was actually
 * given, compared at the precision the reader will see. A server-side copy of
 * these rules would drift from this one silently, and the symptom would be a
 * guard rejecting correct cards — or, far worse, accepting a wrong one.
 *
 * ── A warning before you add a formatter ────────────────────────────────────
 *
 * These functions DO NOT share a locale, and that is deliberate but dangerous:
 *
 *   formatNav(1263.08, 'IDR')  -> '1.263,08'   dot = THOUSANDS
 *   formatNav(1.2027,  'USD')  -> '1.2027'     dot = DECIMAL
 *   formatPctPlain(7.6)        -> '7.60%'      dot = DECIMAL
 *   formatShare(12.4)          -> '12,4%'      comma = DECIMAL
 *
 * So `1.263` is 1263.08 as an IDR NAV and 1.263 as anything else. Never write
 * code that parses these strings back into numbers with one locale rule; the
 * insight guard carries the unit alongside every figure for exactly this reason.
 *
 * Unit conventions come from ./types.js:
 *   - `*Pct` values are already PERCENT (0.52 means +0.52%). Never multiply by 100.
 *   - `aum` is in MILLIONS of the fund's currency.
 *   - `navClose`/`navValue` are unscaled, in the fund's own currency.
 */
import { MONTH_ABBR_ID, MONTH_FULL_ID } from './periods.js';
/** The single "unknown value" glyph. Never `0`, never `-`, never `N/A`. */
export const EM_DASH = '—';
/** True only for a real, finite number. Guards every formatter below. */
function isNum(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
// ─── Currency / NAV ──────────────────────────────────────────────────────────
/**
 * NAV in the fund's own currency, with no Rp / $ prefix.
 * Product (SFL IDR vs Dollar) already names the unit; the sign made tables noisy.
 *   USD → `1.2027` (4 dp)
 *   IDR → `1.263,08` (2 dp, id-ID grouping)
 */
export function formatNav(value, currency) {
    if (!isNum(value))
        return EM_DASH;
    if (currency === 'USD') {
        return value.toFixed(4);
    }
    return value.toLocaleString('id-ID', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}
/**
 * Compact NAV for chart data labels, where horizontal room is scarce.
 *   USD → `1.203` · IDR → `1.263`
 */
export function formatNavCompact(value, currency) {
    if (!isNum(value))
        return EM_DASH;
    return currency === 'USD'
        ? value.toFixed(3)
        : Math.round(value).toLocaleString('id-ID');
}
/** Y-axis tick for the YoY chart. `1.20` / `1.263`. */
export function formatNavAxis(value, currency) {
    if (!isNum(value))
        return EM_DASH;
    return currency === 'USD'
        ? value.toFixed(2)
        : value.toLocaleString('id-ID', { maximumFractionDigits: 0 });
}
// ─── Percent ─────────────────────────────────────────────────────────────────
/**
 * Signed percent, English decimal point — ported verbatim from the reference's
 * `formatPercent` so the ported screens read identically: `+0.52%`, `-1.18%`.
 * (Deliberately NOT localised to id-ID: the reference uses a dot here and the
 * analysts read these numbers alongside the workbook, which does the same.)
 */
export function formatPct(value) {
    if (!isNum(value))
        return EM_DASH;
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}
/** Unsigned percent with two decimals, for KPI figures: `0.52%`. */
export function formatPctPlain(value) {
    if (!isNum(value))
        return EM_DASH;
    return `${value.toFixed(2)}%`;
}
/** Unsigned percent, for cells that carry direction in an icon instead. */
export function formatPctAbs(value) {
    if (!isNum(value))
        return EM_DASH;
    return `${Math.abs(value).toFixed(2)}%`;
}
/** Share-of-AUM style percent: `12,4%` (id-ID, 1 dp). */
export function formatShare(value) {
    if (!isNum(value))
        return EM_DASH;
    return `${value.toLocaleString('id-ID', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })}%`;
}
// ─── AUM ─────────────────────────────────────────────────────────────────────
/**
 * AUM, which the contract stores in MILLIONS of the fund's currency.
 *   245.8 IDR → `Rp 245,8 M`
 *    18.2 USD → `USD 18,2 M`
 *    null     → `—`
 */
export function formatAum(value, currency) {
    if (!isNum(value))
        return EM_DASH;
    const n = value.toLocaleString('id-ID', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    });
    return currency === 'USD' ? `USD ${n} M` : `Rp ${n} M`;
}
// ─── Dates ───────────────────────────────────────────────────────────────────
/**
 * `2023-08-18` → `18 Agu 2023`.
 * Parsed as plain string arithmetic — no Date object, so no timezone can shift
 * the day backwards for a user east of UTC.
 */
export function formatDateId(value) {
    if (!value)
        return EM_DASH;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!m)
        return EM_DASH;
    const month = MONTH_ABBR_ID[Number(m[2]) - 1];
    if (!month)
        return EM_DASH;
    return `${Number(m[3])} ${month} ${m[1]}`;
}
/** `2026-06` → `Juni 2026`. Falls back to the raw period if malformed. */
export function formatPeriodLongId(value) {
    if (!value)
        return EM_DASH;
    const m = /^(\d{4})-(\d{2})$/.exec(value);
    if (!m)
        return value;
    return `${MONTH_FULL_ID[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
}
/** `2026-06` → `Jun 2026` / `2025-12` → `Des 2025`. */
export function formatPeriodId(value) {
    if (!value)
        return EM_DASH;
    const m = /^(\d{4})-(\d{2})$/.exec(value);
    if (!m)
        return value;
    return `${MONTH_ABBR_ID[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
}
/**
 * ISO-8601 UTC timestamp → `30 Jun 2026, 16:05` in the viewer's local zone.
 * Used for the header freshness stamp and the `Diperbarui` column.
 */
export function formatTimestamp(value) {
    if (!value)
        return EM_DASH;
    const d = new Date(value);
    if (Number.isNaN(d.getTime()))
        return EM_DASH;
    const day = d.getDate();
    const month = MONTH_ABBR_ID[d.getMonth()];
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${d.getFullYear()}, ${hh}:${mm}`;
}
/** Plain ISO date passthrough that still guards against null. */
export function formatIsoDate(value) {
    return value && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : EM_DASH;
}
// ─── Misc ────────────────────────────────────────────────────────────────────
/** `12` → `12 Bulan`; null → `—`. Drives the Frekuensi tile. */
export function formatFrequency(months) {
    if (!isNum(months))
        return EM_DASH;
    return `${months} Bulan`;
}
/** Any non-empty string, else an em dash. For nullable text from the API. */
export function orDash(value) {
    return value != null && value.trim() !== '' ? value : EM_DASH;
}
export function trendOf(value) {
    if (!isNum(value))
        return 'none';
    if (value > 0)
        return 'up';
    if (value < 0)
        return 'down';
    return 'flat';
}
//# sourceMappingURL=format.js.map